import { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { fetchTenants, queryKeys } from '../lib/api';
import type { TenantListParams } from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

const PAGE_SIZE = 25;

type SortField = 'created_at' | 'display_name' | 'status';
type SortDir = 'asc' | 'desc';

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'trial':
      return 'info';
    case 'suspended':
      return 'warning';
    case 'deleted':
      return 'danger';
    default:
      return 'neutral';
  }
}

function SortIcon({
  field,
  sort,
  dir,
}: {
  field: SortField;
  sort: SortField | undefined;
  dir: SortDir;
}) {
  if (sort !== field) return <ChevronsUpDown size={13} className="opacity-40" />;
  return dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
}

export function TenantsListPage() {
  const navigate = useNavigate();
  const { hasPerm } = useAdminAuth();

  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortField>('created_at');
  const [dir, setDir] = useState<SortDir>('desc');
  const [offset, setOffset] = useState(0);

  // Debounce search 300ms
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(rawSearch);
      setOffset(0);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rawSearch]);

  const params: TenantListParams = { sort, dir, limit: PAGE_SIZE, offset };
  if (search) params.search = search;

  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.tenants(params),
    queryFn: () => fetchTenants(params),
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function toggleSort(field: SortField) {
    if (sort === field) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setDir('asc');
    }
    setOffset(0);
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-heading-2 text-primary">Tenants</h1>
          <p className="mt-1 text-body-sm text-muted">
            {total > 0 ? `${total} tenants` : 'Manage tenant accounts.'}
          </p>
        </div>
        {hasPerm(PERMS.TENANTS_CREATE) && (
          <Button variant="primary" onClick={() => void navigate({ to: '/tenants/new' })}>
            New tenant
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2.5">
        <div className="flex-1 basis-70 max-w-90">
          <Input
            type="search"
            placeholder="Search tenants…"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            aria-label="Search tenants"
          />
        </div>
      </div>

      {/* Error state */}
      {isError && !isPending && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
        >
          {error instanceof Error ? error.message : 'Failed to load tenants.'}
        </div>
      )}

      {/* Empty state — only show once we have a definitive empty result */}
      {!isPending && !isError && rows.length === 0 && (
        <EmptyState
          title={search ? 'No tenants found' : 'No tenants yet'}
          description={
            search
              ? `No results for "${search}". Try a different search.`
              : 'Tenant accounts will appear here once created.'
          }
        />
      )}

      {/* Table — skeleton rows during first load; real rows (or prior rows via keepPreviousData) otherwise */}
      {(isPending || (!isError && rows.length > 0)) && (
        <>
          <div className="overflow-x-auto">
            <Table dense>
              <TableHead>
                <tr>
                  <TableHeaderCell>
                    {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: table sort-header toggle; Button's fill/border/size fight the column header */}
                    <button
                      type="button"
                      onClick={() => toggleSort('display_name')}
                      className="inline-flex items-center gap-1 bg-transparent border-none cursor-pointer text-inherit font-inherit p-0"
                    >
                      Name <SortIcon field="display_name" sort={sort} dir={dir} />
                    </button>
                  </TableHeaderCell>
                  <TableHeaderCell>Slug</TableHeaderCell>
                  <TableHeaderCell>
                    {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: table sort-header toggle; Button's fill/border/size fight the column header */}
                    <button
                      type="button"
                      onClick={() => toggleSort('status')}
                      className="inline-flex items-center gap-1 bg-transparent border-none cursor-pointer text-inherit font-inherit p-0"
                    >
                      Status <SortIcon field="status" sort={sort} dir={dir} />
                    </button>
                  </TableHeaderCell>
                  <TableHeaderCell className="px-3 py-2 text-right">Locations</TableHeaderCell>
                  <TableHeaderCell className="px-3 py-2 text-right">Profit Centers</TableHeaderCell>
                  <TableHeaderCell className="px-3 py-2 text-right">Terminals</TableHeaderCell>
                  <TableHeaderCell className="px-3 py-2 text-right">Users</TableHeaderCell>
                  <TableHeaderCell>
                    {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: table sort-header toggle; Button's fill/border/size fight the column header */}
                    <button
                      type="button"
                      onClick={() => toggleSort('created_at')}
                      className="inline-flex items-center gap-1 bg-transparent border-none cursor-pointer text-inherit font-inherit p-0"
                    >
                      Created <SortIcon field="created_at" sort={sort} dir={dir} />
                    </button>
                  </TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {isPending ? (
                  <SkeletonTable rows={6} cols={8} />
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() =>
                        void navigate({ to: '/tenants/$tenantId', params: { tenantId: row.id } })
                      }
                    >
                      <TableCell>
                        <span className="text-body-sm-emphasis text-primary">
                          {row.displayName}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-caption text-muted">{row.slug}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2 align-middle text-right tabular-nums text-secondary">
                        {row.locationsCount}
                      </TableCell>
                      <TableCell className="px-3 py-2 align-middle text-right tabular-nums text-secondary">
                        {row.profitCentersCount}
                      </TableCell>
                      <TableCell className="px-3 py-2 align-middle text-right tabular-nums text-secondary">
                        {row.terminalsCount}
                      </TableCell>
                      <TableCell className="px-3 py-2 align-middle text-right tabular-nums text-secondary">
                        {row.usersCount}
                      </TableCell>
                      <TableCell className="text-caption text-muted">
                        {new Date(row.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {!isPending && totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <span className="text-body-sm text-muted">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
